import { CustomPopover } from "@/components/popover/CustomPopover";
import { SelectComponent } from "@/components/select/Select";
import DatePicker from "@/components/date-picker";

import {
  AsyncAutocompleteSelect,
  Option,
} from "@/components/select/AsyncAutocompleteSelect";
import { AppColors } from "@/styles/colors";

import {
  Badge,
  Box,
  Button,
  Field,
  Flex,
  Grid,
  GridItem,
  HStack,
  Text,
} from "@chakra-ui/react";
import dayjs from "dayjs";
import { Filter } from "lucide-react";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { Control, FieldValues, UseFormHandleSubmit } from "react-hook-form";
import { getCuentasByBusqueda } from "@/services/accounts.service";
import { getTaxTypes } from "@/services/taxes.service";
import type { TaxType } from "@/pages/config/taxes/schema/taxes.schema";
import { EstadosImpuestosBIND } from "@/constants/enums/status/entity-status-ids";
import { LoadedTaxesStatus } from "@/constants/entity-status";

interface Props {
  control: Control<FieldValues>;
  handleSubmit: UseFormHandleSubmit<FieldValues>;
  openFilters: boolean;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  submitFilters: (event: FieldValues) => Promise<void>;
  clearFilters: () => Promise<void>;
  disableFilters?: boolean;
  loadingFilters?: boolean;
  filtersApplied?: boolean;
  disableClear?: boolean;
  disableApply?: boolean;
}

export const TaxesLoadedFilter = ({
  openFilters,
  disableFilters,
  loadingFilters,
  filtersApplied,
  disableClear,
  submitFilters,
  handleSubmit,
  control,
  setOpenFilters,
  disableApply,
  clearFilters,
}: Props) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isActiveRef = useRef(false);

  const [loadingAccounts, setLoadingAccounts] = useState<boolean>(false);
  const [taxTypes, setTaxTypes] = useState<TaxType[]>([]);
  const [loadingTaxTypes, setLoadingTaxTypes] = useState<boolean>(false);

  useEffect(() => {
    if (isActiveRef.current) return;
    isActiveRef.current = true;

    const loadTaxTypes = async () => {
      setLoadingTaxTypes(true);
      try {
        const types = await getTaxTypes();
        setTaxTypes(types ?? []);
      } catch {
        setTaxTypes([]);
      } finally {
        setLoadingTaxTypes(false);
      }
    };

    loadTaxTypes();
  }, []);

  const getOptions = async (inputValue: string) => {
    const filtered = await getCuentasByBusqueda({
      busqueda: inputValue,
    });

    return (
      filtered?.map((p) => ({
        label: `${p.titular} - ${p.nroCuenta} - ${p.tipoCuenta}`,

        value: p.nroCuenta,
      })) ?? []
    );
  };

  const loadOptions = (inputValue: string): Promise<Option[]> => {
    return new Promise((resolve) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef?.current);
      }

      timeoutRef.current = setTimeout(() => {
        if (inputValue.trim().length < 3) {
          resolve([]);

          return;
        }

        setLoadingAccounts(true);

        const options = getOptions(inputValue);

        setLoadingAccounts(false);

        resolve(options);
      }, 1000);
    });
  };

  return (
    <Flex width="100%" paddingY="10px" justifyContent="flex-end">
      <HStack gap="10">
        <CustomPopover
          open={openFilters}
          onOpenChange={(e) => setOpenFilters(e.open)}
          triggerComponent={
            <Box position="relative" display="inline-block">
              <Button
                variant="outline"
                paddingX="12px"
                height="32px"
                disabled={disableFilters || loadingFilters}
              >
                <Filter />

                <Text fontWeight={600} fontSize="14px">
                  Filtrar
                </Text>
              </Button>

              {filtersApplied && (
                <Badge
                  position="absolute"
                  top="-4px"
                  right="-4px"
                  bg="red.500"
                  color="white"
                  fontSize="10px"
                  borderRadius="full"
                  px="2"
                  py="0.5"
                >
                  !
                </Badge>
              )}
            </Box>
          }
          bodyComponent={
            <Flex
              as="form"
              flexDir="column"
              padding="25px"
              gap="15px"
              onSubmit={handleSubmit(submitFilters)}
            >
              <Flex alignItems="center" justifyContent="space-between">
                <Text fontWeight={600} fontSize="20px">
                  Filtrar
                </Text>

                <Button
                  variant="plain"
                  color={AppColors.BLUE_TEXT}
                  fontWeight={600}
                  onClick={clearFilters}
                  disabled={loadingFilters || disableClear}
                >
                  Limpiar filtros
                </Button>
              </Flex>

              <Grid templateColumns="repeat(2, 1fr)" gap={4}>


                <GridItem>
                  <Box maxWidth="280px">
                    <DatePicker
                      control={control}
                      labelText="Fecha"
                      name="fecha"
                      placeholder="Ingresar fecha"
                      maxDate={dayjs()}
                      disabled={disableFilters || loadingFilters}
                    />
                  </Box>
                </GridItem>

                <GridItem>

                  <Field.Root>
                    <Field.Label>
                      <Text fontWeight={600}>Cuenta</Text>
                    </Field.Label>
                    <Box width="280px">

                      <AsyncAutocompleteSelect
                        control={control}
                        loadOptions={loadOptions}
                        name="cuenta"
                        placeholder="Buscar..."
                        isLoading={loadingAccounts}
                        disabled={
                          disableFilters || loadingFilters || loadingAccounts
                        }
                        closeMenuOnSelect
                        isClearable
                        triggerWithLength
                      />
                    </Box>

                  </Field.Root>
                </GridItem>

                <GridItem>
                  <Box maxWidth="280px">

                    <SelectComponent
                      name="tipoImpuesto"
                      items={taxTypes.map((t) => ({
                        label: t.nombre,
                        value: String(t.id),
                      }))}
                      labelText="Tipo de impuesto"
                      placeholder="Seleccionar"
                      control={control}
                      disabled={disableFilters || loadingFilters}
                      loading={loadingTaxTypes}
                      menuMaxHeight="250px"
                      minHeight="42px"
                      clearable
                      multiple
                    />
                  </Box>
                </GridItem>

                <GridItem>
                  <Box maxWidth="280px">

                    <SelectComponent
                      name="estado"
                      items={[
                        {
                          label: LoadedTaxesStatus.PENDING,
                          value: String(EstadosImpuestosBIND.Pendiente),
                        },
                        {
                          label: LoadedTaxesStatus.PAID,
                          value: String(EstadosImpuestosBIND.Cobrado),
                        },
                        {
                          label: LoadedTaxesStatus.ERROR,
                          value: String(EstadosImpuestosBIND.Error),
                        },
                      ]}
                      labelText="Estado"
                      placeholder="Seleccionar"
                      control={control}
                      disabled={disableFilters || loadingFilters}
                      menuMaxHeight="250px"
                      minHeight="42px"
                      clearable
                      multiple
                    />
                  </Box>
                </GridItem>
              </Grid>

              <Button
                bgColor={AppColors.GREEN_PRIMARY}
                type="submit"
                loading={loadingFilters}
                disabled={
                  loadingFilters || disableFilters || Boolean(disableApply)
                }
              >
                Aplicar filtros
              </Button>
            </Flex>
          }
        />
      </HStack>
    </Flex>
  );
};
